import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Activity, Brain, Cpu, Filter, Loader2, RefreshCw, Signal } from "lucide-react";
import { analyzeLiveMesh, fetchLiveMesh, type LiveMeshSignal } from "@/lib/intel/live/live.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const SEV_TONE: Record<string, string> = {
  critical: "text-danger",
  high: "text-amber",
  medium: "text-cyan",
  low: "text-muted-foreground",
};

export function LiveMeshPanel({ paused }: { paused: boolean }) {
  const [category, setCategory] = useState<string | null>(null);
  const [minPriority, setMinPriority] = useState(0);
  const meshFn = useServerFn(fetchLiveMesh);
  const briefFn = useServerFn(analyzeLiveMesh);

  const mesh = useQuery({
    queryKey: ["live-mesh", category],
    queryFn: () => meshFn({ data: { size: 140, ...(category ? { categories: [category] } : {}) } }),
    refetchInterval: paused ? false : 90_000,
    staleTime: 45_000,
  });

  const brief = useMutation({
    mutationFn: () =>
      briefFn({
        data: {
          environment: "live" as const,
          signals: (mesh.data?.signals ?? []).slice(0, 40).map((s) => ({
            source: s.source,
            title: s.title,
            url: s.url,
            ts: s.ts,
            snippet: s.snippet,
            severity: s.severity,
            priority: s.priority,
            corroboration: s.corroboration,
          })),
        },
      }),
  });

  const coverage = mesh.data?.coverage;
  const signals: LiveMeshSignal[] = useMemo(
    () => (mesh.data?.signals ?? []).filter((s) => s.priority >= minPriority),
    [mesh.data, minPriority],
  );

  return (
    <div className="space-y-3">
      <div className="glass rounded-xl p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Live sources · ranked by priority</div>
            <p className="mt-1 text-xs text-muted-foreground">
              {coverage
                ? `${coverage.catalogueSize.toLocaleString()} live endpoints in catalogue · ${coverage.sampled} polled this cycle · ${coverage.responded} returned data · ${coverage.failed} unreachable · ${coverage.wallMs}ms`
                : "Polling the live endpoint mesh…"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => mesh.refetch()} disabled={mesh.isFetching}>
              <RefreshCw className={`mr-1 h-3.5 w-3.5 ${mesh.isFetching ? "animate-spin" : ""}`} /> Sweep
            </Button>
            <Button size="sm" onClick={() => brief.mutate()} disabled={brief.isPending || !signals.length}>
              {brief.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Brain className="mr-1 h-3.5 w-3.5" />}
              AI triage brief
            </Button>
          </div>
        </div>

        {mesh.isError && (
          <div className="mt-3 rounded-md border border-danger/40 bg-danger/10 p-3 text-xs text-danger">
            The mesh sweep failed. No signals are shown rather than placeholder data.
          </div>
        )}

        {coverage && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              onClick={() => setCategory(null)}
              className={`rounded-md border px-2 py-1 font-mono text-[10px] uppercase ${category === null ? "border-cyan text-cyan" : "border-panel-border text-muted-foreground"}`}
            >
              <Filter className="mr-1 inline h-3 w-3" /> all
            </button>
            {coverage.catalogueByCategory.map((c) => (
              <button
                key={c.category}
                onClick={() => setCategory(c.category)}
                className={`rounded-md border px-2 py-1 font-mono text-[10px] uppercase ${category === c.category ? "border-cyan text-cyan" : "border-panel-border text-muted-foreground"}`}
              >
                {c.category} · {c.count.toLocaleString()}
              </button>
            ))}
          </div>
        )}

        <div className="mt-3 flex items-center gap-3">
          <Cpu className="h-4 w-4 text-cyan" />
          <label className="font-mono text-[10px] uppercase text-muted-foreground">Min priority {minPriority}</label>
          <input
            type="range"
            min={0}
            max={90}
            step={5}
            value={minPriority}
            onChange={(e) => setMinPriority(Number(e.target.value))}
            className="h-1 flex-1 accent-cyan"
            aria-label="Minimum priority score"
          />
          <span className="font-mono text-[10px] text-muted-foreground">{signals.length} signals</span>
        </div>
      </div>

      {brief.data && (
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
            <Brain className="h-3.5 w-3.5" /> Grok triage brief · {brief.data.analyzed} signals analysed
          </div>
          <pre className="mt-3 whitespace-pre-wrap break-words font-sans text-xs leading-relaxed text-foreground">{brief.data.brief}</pre>
          {brief.data.citations.length > 0 && (
            <div className="mt-3 space-y-1">
              {brief.data.citations.map((c, i) => (
                <a key={c} href={c} target="_blank" rel="noopener noreferrer" className="block truncate font-mono text-[10px] text-cyan hover:underline">
                  [{i + 1}] {c}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
      {brief.isError && (
        <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-xs text-danger">
          The analysis layer is unavailable right now, so no brief is asserted.
        </div>
      )}

      <div className="glass rounded-xl p-3">
        <div className="flex items-center gap-2 px-1 pb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
          <Signal className="h-3.5 w-3.5" /> Prioritised live signals
        </div>
        <div className="divide-y divide-panel-border overflow-hidden rounded-md border border-panel-border">
          {mesh.isLoading && <div className="p-6 text-center text-sm text-muted-foreground">Sweeping live endpoint mesh…</div>}
          {!mesh.isLoading && !signals.length && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <Activity className="mx-auto mb-2 h-5 w-5 text-cyan" />
              No signals cleared the current threshold in this window.
            </div>
          )}
          {signals.map((s) => (
            <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 hover:bg-panel/60">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`font-mono ${SEV_TONE[s.severity]}`}>{String(s.priority).padStart(2, "0")}</span>
                <Badge variant={s.severity === "critical" || s.severity === "high" ? "destructive" : "secondary"}>{s.severity}</Badge>
                <span className="min-w-0 flex-1 truncate">{s.title}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{s.ts.slice(11, 16)}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                <span className="font-mono text-cyan">{s.source}</span>
                <span>· {s.category}</span>
                {s.corroboration > 1 && <span>· {s.corroboration} independent sources</span>}
                {s.drivers.map((d) => (
                  <span key={d} className="rounded border border-panel-border px-1 py-0.5">{d}</span>
                ))}
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
